(() => {
  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class LottiePlayerLite extends HTMLElement {
    static get observedAttributes() {
      return ['src','autoplay','loop','speed','hover','renderer','mode','background'];
    }

    constructor() {
      super();
      this._player = null;
      this._container = null;
      this._ready = false;
      this._io = null;
      this._onEnter = null;
      this._onLeave = null;
    }

    connectedCallback() {
      // Avoid double-init if Shopify re-renders sections
      if (this.shadowRoot) return;

      const root = this.attachShadow({ mode: 'open' });

      const style = document.createElement('style');
      style.textContent = `
        :host { display: inline-block; }
        .wrap { position: relative; width: 100%; height: 100%; }
        .anim { width: 100%; height: 100%; }
        button {
          position: absolute; right: 10px; bottom: 10px;
          border: 0; border-radius: 999px;
          padding: 8px 10px;
          background: rgba(0,0,0,.6);
          color: white; cursor: pointer;
          font: 12px/1 system-ui, -apple-system, Segoe UI, Roboto, Arial;
          display: none;
        }
        :host([controls]) button { display: inline-flex; align-items: center; gap: 6px; }
      `;

      const wrap = document.createElement('div');
      wrap.className = 'wrap';

      const anim = document.createElement('div');
      anim.className = 'anim';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = 'Pause';
      btn.addEventListener('click', () => {
        if (!this._player) return;
        if (this._player.isPaused) {
          this._player.play();
          btn.textContent = 'Pause';
        } else {
          this._player.pause();
          btn.textContent = 'Play';
        }
      });

      wrap.appendChild(anim);
      wrap.appendChild(btn);
      root.appendChild(style);
      root.appendChild(wrap);

      this._container = anim;

      // lottie-web must be present
      if (!window.lottie) {
        console.error('[lottie-player-lite] window.lottie not found. Did you include lottie.min.js?');
        return;
      }

      this._init();
    }

    disconnectedCallback() {
      this._cleanup();
    }

    attributeChangedCallback() {
      if (!this._ready) return;
      this._reinit();
    }

    _getBoolAttr(name) {
      return this.hasAttribute(name);
    }

    _getAttr(name, fallback = null) {
      const v = this.getAttribute(name);
      return v === null ? fallback : v;
    }

    _config() {
      const src = this._getAttr('src');
      const renderer = this._getAttr('renderer', 'svg'); // svg | canvas
      const loop = this._getBoolAttr('loop');
      const autoplay = this._getBoolAttr('autoplay');
      const speed = parseFloat(this._getAttr('speed', '1')) || 1;
      const hover = this._getBoolAttr('hover');
      const mode = this._getAttr('mode', 'always'); // always | inview
      const background = this._getAttr('background', 'transparent');

      return { src, renderer, loop, autoplay, speed, hover, mode, background };
    }

    _init() {
      const { src, renderer, loop, autoplay, speed, hover, mode, background } = this._config();
      if (!src || !this._container) return;

      const effectiveAutoplay = prefersReducedMotion() ? false : autoplay;

      this._player = window.lottie.loadAnimation({
        container: this._container,
        renderer,
        loop,
        autoplay: mode === 'inview' ? false : effectiveAutoplay,
        path: src,
        rendererSettings: {
          clearCanvas: true,
          progressiveLoad: true,
          preserveAspectRatio: 'xMidYMid meet'
        }
      });

      this._player.setSpeed(speed);
      this.shadowRoot.host.style.background = background;

      if (hover) {
        this._onEnter = () => { if (this._player && !prefersReducedMotion()) this._player.play(); };
        this._onLeave = () => { if (this._player) this._player.pause(); };
        this.addEventListener('mouseenter', this._onEnter);
        this.addEventListener('mouseleave', this._onLeave);
      }

      if (mode === 'inview') {
        this._io = new IntersectionObserver((entries) => {
          for (const e of entries) {
            if (!this._player) return;
            if (e.isIntersecting) {
              if (!prefersReducedMotion() && effectiveAutoplay) this._player.play();
            } else {
              this._player.pause();
            }
          }
        }, { threshold: 0.25 });
        this._io.observe(this);
      }

      this._ready = true;
    }

    _cleanup() {
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
      if (this._onEnter) this.removeEventListener('mouseenter', this._onEnter);
      if (this._onLeave) this.removeEventListener('mouseleave', this._onLeave);
      this._onEnter = this._onLeave = null;

      if (this._player) {
        try { this._player.destroy(); } catch (e) {}
        this._player = null;
      }
      this._ready = false;
    }

    _reinit() {
      this._cleanup();
      this._init();
    }
  }

  if (!customElements.get('lottie-player-lite')) {
    customElements.define('lottie-player-lite', LottiePlayerLite);
  }
})();
